import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { colors, spacing, typography } from '../design-system/tokens'
import { useWhiteLabel } from '../hooks/useWhiteLabel'

// ── Animations ───────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`

const arrowBounce = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(2px); }
`

const platformFadeIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`

// ── SVG Icons ────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
)

const WorkloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="2" y="3" width="16" height="14" rx="2" />
    <path d="M2 7h16M7 7v10" />
  </svg>
)

const DependencyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="5" cy="10" r="2.5" />
    <circle cx="15" cy="5" r="2.5" />
    <circle cx="15" cy="15" r="2.5" />
    <path d="M7.5 9l5-3M7.5 11l5 3" />
  </svg>
)

const BlueprintIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M4 17V7l6-4 6 4v10" />
    <path d="M4 17h12" />
    <path d="M8 17v-5h4v5" />
  </svg>
)

// ── Types ────────────────────────────────────────────────────
interface LandingPageProps {
  onGetStarted: () => void
}

// ── Shared button focus style ────────────────────────────────
const focusVisible = {
  '&:focus-visible': {
    outline: `2px solid ${alpha(colors.accent.main, 0.9)}`,
    outlineOffset: '3px',
    boxShadow: `0 0 0 6px ${alpha(colors.accent.main, 0.12)}`,
  },
}

// ── Component ────────────────────────────────────────────────
const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [mounted, setMounted] = useState(false)
  const { whiteLabelSettings, isWhiteLabelEnabled } = useWhiteLabel()

  useEffect(() => { setMounted(true) }, [])

  const prioritySources = React.useMemo(
    () => ['Snowflake', 'AWS Redshift', 'Microsoft SQL Server', 'Oracle', 'Azure Synapse'],
    [],
  )

  const targetPlatforms = React.useMemo(
    () => ['GCP BigQuery', 'Delta Lake', 'Databricks'],
    [],
  )

  const navLinks = React.useMemo(
    () => [
      { label: 'Platform', href: '#platform' },
      { label: 'Workflow', href: '#workflow' },
      { label: 'About', href: '#capabilities' },
    ],
    [],
  )

  const capabilities = React.useMemo(() => [
    {
      icon: <WorkloadIcon />,
      title: 'Source-System Intelligence',
      desc: 'Assess Snowflake, AWS Redshift, Microsoft SQL Server, Oracle, and Azure Synapse workloads before modernization. Stored procedures, ETL patterns, and batch jobs are parsed and scored before a single line moves.',
    },
    {
      icon: <DependencyIcon />,
      title: 'Dependency Mapping',
      desc: 'Trace table, view, procedure, and job dependencies across the source estate. See what feeds what before building BigQuery, Delta Lake, or Databricks migration waves.',
    },
    {
      icon: <BlueprintIcon />,
      title: 'Target Roadmaps',
      desc: 'Generate migration blueprints, conversion rules, wave plans, and readiness evidence for BigQuery, Delta Lake, and Databricks delivery teams. Every decision remains reviewable.',
    },
  ], [])

  const steps = React.useMemo(() => [
    { num: '1', title: 'Prioritize the source estate', desc: 'Start with the five roadmap systems: Snowflake, AWS Redshift, Microsoft SQL Server, Oracle, and Azure Synapse.' },
    { num: '2', title: 'Analyze migration readiness', desc: 'Score complexity, map dependencies, identify conversion blockers, and shape the backlog for BigQuery, Delta Lake, or Databricks delivery.' },
    { num: '3', title: 'Build the pipeline', desc: 'Produce wave plans, conversion work packages, and reviewable artifacts so delivery and alliance teams can move quickly.' },
  ], [])

  const anim = (delay: number) =>
    mounted && !prefersReducedMotion ? `${fadeUp} 0.8s ease-out ${delay}s both` : 'none'

  return (
    <Box
      sx={{
        background: colors.background.surface,
        minHeight: '100vh',
        overflowX: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at top left, ${alpha(colors.accent.light, 0.95)} 0%, transparent 34%),
            radial-gradient(circle at 82% 18%, ${alpha(colors.info.light, 0.28)} 0%, transparent 24%),
            linear-gradient(180deg, ${alpha(colors.background.surface, 0)} 0%, ${alpha(colors.background.page, 0.55)} 100%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* ── NAV ─────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: spacing.zIndex.sticky,
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 3, md: 7 },
          background: alpha(colors.background.surface, 0.82),
          backdropFilter: 'blur(18px) saturate(1.25)',
          borderBottom: `1px solid ${alpha(colors.border.subtle, 0.92)}`,
          boxShadow: `0 8px 28px ${alpha(colors.neutral[900], 0.04)}`,
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            textDecoration: 'none',
            borderRadius: spacing.borderRadius.sm,
            ...focusVisible,
          }}
        >
          <img
            src="/archer_wordmark.svg"
            alt="Archer"
            style={{ height: '42px', objectFit: 'contain' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box
          component="nav"
          aria-label="Main navigation"
          sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5, md: 4.5 } }}
        >
          {navLinks.map((link) => (
            <Typography
              key={link.label}
              component="a"
              href={link.href}
              sx={{
                fontSize: { xs: typography.fontSize.xs, sm: typography.fontSize.sm },
                color: colors.text.secondary,
                fontWeight: typography.fontWeight.regular,
                fontFamily: typography.fontFamily.primary,
                textDecoration: 'none',
                letterSpacing: typography.letterSpacing.wide,
                transition: `color ${spacing.transitions.fast}, transform ${spacing.transitions.fast}`,
                '&:hover': {
                  color: colors.text.primary,
                  transform: 'translateY(-1px)',
                },
                ...focusVisible,
              }}
            >
              {link.label}
            </Typography>
          ))}
          <Box
            component="button"
            onClick={onGetStarted}
            sx={{
              fontSize: { xs: typography.fontSize.xs, sm: typography.fontSize.sm },
              color: colors.text.onPrimary,
              fontWeight: typography.fontWeight.medium,
              fontFamily: typography.fontFamily.primary,
              py: { xs: '11px', sm: '9px' },
              px: { xs: '20px', sm: '24px' },
              minHeight: { xs: '44px', sm: 'auto' },
              background: colors.text.primary,
              border: 'none',
              borderRadius: spacing.borderRadius.button,
              cursor: 'pointer',
              letterSpacing: typography.letterSpacing.wide,
              transition: `background ${spacing.transitions.normal}, transform ${spacing.transitions.normal}, box-shadow ${spacing.transitions.normal}`,
              '&:hover': {
                background: colors.neutral[700],
                transform: 'translateY(-1px)',
                boxShadow: `0 12px 28px ${alpha(colors.neutral[900], 0.12)}`,
              },
              boxShadow: `0 8px 22px ${alpha(colors.neutral[900], 0.1)}`,
              ...focusVisible,
            }}
          >
            Sign In to Archer
          </Box>
        </Box>
      </Box>

      {/* ── HERO ────────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          pt: { xs: '120px', md: '140px' },
          pb: { xs: 7, md: 10 },
          px: { xs: 3, md: 7 },
          position: 'relative',
        }}
      >
        <Box sx={{ animation: anim(0), maxWidth: '820px' }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: { xs: '2.5rem', sm: '3.2rem', md: 'clamp(42px, 5.5vw, 68px)' },
              fontWeight: typography.fontWeight.bold,
              lineHeight: 1.12,
              letterSpacing: 0,
              color: colors.text.primary,
              mb: 3,
              px: { xs: 2, md: 0 },
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              textWrap: 'balance',
            }}
          >
            Your agentic workbench{!isMobile && <br />} for data warehouse modernization
            <Box component="span" sx={{ color: colors.accent.main }}>.</Box>
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: typography.fontFamily.primary,
            fontSize: { xs: '1rem', md: '18px' },
            lineHeight: 1.75,
            color: colors.text.secondary,
            maxWidth: '580px',
            fontWeight: typography.fontWeight.regular,
            letterSpacing: typography.letterSpacing.normal,
            mb: { xs: 4.5, md: 5.5 },
            animation: anim(0.15),
            px: { xs: 2, md: 0 },
            textWrap: 'pretty',
          }}
        >
          Any-to-any migration intelligence for Snowflake, AWS Redshift, Microsoft SQL Server,
          Oracle, and Azure Synapse sources moving to GCP BigQuery, Delta Lake, and Databricks.
        </Typography>

        <Box
          component="button"
          onClick={onGetStarted}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.25,
            py: { xs: 2, md: '15px' },
            px: { xs: 3.5, md: '36px' },
            minHeight: { xs: '48px', md: 'auto' },
            fontSize: typography.fontSize.md,
            fontWeight: typography.fontWeight.semibold,
            fontFamily: typography.fontFamily.primary,
            color: colors.text.onPrimary,
            background: colors.accent.main,
            border: 'none',
            borderRadius: spacing.borderRadius.lg,
            cursor: 'pointer',
            transition: `background ${spacing.transitions.normal}, transform ${spacing.transitions.normal}, box-shadow ${spacing.transitions.normal}`,
            letterSpacing: typography.letterSpacing.wide,
            animation: anim(0.3),
            '&:hover': {
              background: colors.accent.hover,
              transform: 'translateY(-1px)',
              boxShadow: `0 12px 36px ${alpha(colors.accent.main, 0.24)}`,
            },
            '&:active': { transform: 'translateY(0)' },
            '& svg': {
              transition: `transform ${spacing.transitions.fast}`,
              animation: prefersReducedMotion ? 'none' : `${arrowBounce} 2s ease-in-out infinite`,
            },
            '&:hover svg': {
              transform: 'translateX(2px)',
              animation: 'none',
            },
            boxShadow: `0 8px 24px ${alpha(colors.accent.main, 0.2)}`,
            ...focusVisible,
          }}
        >
          Enter Platform <ArrowIcon />
        </Box>
      </Box>

      {/* ── PROOF POINTS ────────────────────────────────── */}
      <Box
        component="section"
        aria-label="Proof points"
        sx={{
          textAlign: 'center',
          py: { xs: 3, md: 4 },
          animation: anim(0.45),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 2, md: 5 },
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: typography.fontSize.sm, md: typography.fontSize.md },
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.medium,
              letterSpacing: typography.letterSpacing.wide,
              color: colors.text.secondary,
              transition: `all ${spacing.transitions.normal}`,
              cursor: 'default',
              px: 1.75,
              py: 1,
              borderRadius: spacing.borderRadius.full,
              background: alpha(colors.background.card, 0.75),
              border: `1px solid ${alpha(colors.border.primary, 0.88)}`,
              '&:hover': {
                color: colors.text.primary,
                transform: 'translateY(-1px)',
                boxShadow: spacing.shadow.sm,
              },
            }}
          >
            Prioritize the top source systems for active migration demand
          </Typography>
          <Box
            aria-hidden="true"
            sx={{
              width: '1px',
              height: '16px',
              background: colors.border.primary,
              display: { xs: 'none', md: 'block' },
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: typography.fontSize.sm, md: typography.fontSize.md },
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.medium,
              letterSpacing: typography.letterSpacing.wide,
              color: colors.text.secondary,
              transition: `all ${spacing.transitions.normal}`,
              cursor: 'default',
              px: 1.75,
              py: 1,
              borderRadius: spacing.borderRadius.full,
              background: alpha(colors.background.card, 0.75),
              border: `1px solid ${alpha(colors.border.primary, 0.88)}`,
              '&:hover': {
                color: colors.text.primary,
                transform: 'translateY(-1px)',
                boxShadow: spacing.shadow.sm,
              },
            }}
          >
            Turn assessment evidence into a delivery roadmap
          </Typography>
        </Box>
      </Box>

      {/* ── SUPPORTED PLATFORMS ─────────────────────────── */}
      <Box
        component="section"
        id="platform"
        aria-label="Supported platforms"
        sx={{
          textAlign: 'center',
          pt: { xs: 2, md: 3 },
          pb: 2,
          animation: anim(0.4),
        }}
      >
        <Typography
          sx={{
            fontSize: typography.fontSize.xs,
            fontFamily: typography.fontFamily.primary,
            fontWeight: typography.fontWeight.medium,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.text.tertiary,
            mb: 4,
          }}
        >
          Any-to-Any Migration Roadmap
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: { xs: 4, md: 6 },
            alignItems: { xs: 'center', md: 'start' },
            justifyContent: 'center',
            maxWidth: '900px',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 4 },
            borderRadius: '28px',
            background: alpha(colors.background.card, 0.78),
            border: `1px solid ${alpha(colors.border.primary, 0.72)}`,
            boxShadow: `0 14px 36px ${alpha(colors.neutral[900], 0.05)}`,
          }}
        >
          {/* SOURCE PLATFORMS */}
          <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
            <Typography
              sx={{
                fontSize: typography.fontSize.xs,
                fontFamily: typography.fontFamily.primary,
                fontWeight: typography.fontWeight.semibold,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: colors.text.tertiary,
                mb: 2,
              }}
            >
              Priority Source Systems
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'center', md: 'flex-end' } }}>
              {prioritySources.map((platform, i) => (
                <Box
                  key={platform}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    animation: mounted && !prefersReducedMotion ? `${platformFadeIn} 0.5s ease-out ${0.5 + i * 0.1}s both` : 'none',
                    transition: `all ${spacing.transitions.normal}`,
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateX(-4px)',
                    },
                    '&:hover .platform-dot': {
                      transform: 'scale(1.5)',
                      background: colors.accent.main,
                    },
                    '&:hover .platform-name': {
                      color: colors.text.primary,
                      fontWeight: typography.fontWeight.semibold,
                    },
                  }}
                >
                  <Box
                    className="platform-dot"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: spacing.borderRadius.full,
                      background: colors.text.secondary,
                      flexShrink: 0,
                      transition: `all ${spacing.transitions.normal}`,
                    }}
                  />
                  <Typography
                    className="platform-name"
                    sx={{
                      fontSize: { xs: typography.fontSize.sm, md: typography.fontSize.md },
                      fontFamily: typography.fontFamily.primary,
                      fontWeight: typography.fontWeight.medium,
                      letterSpacing: typography.letterSpacing.wide,
                      color: colors.text.secondary,
                      transition: `all ${spacing.transitions.normal}`,
                    }}
                  >
                    {platform}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ARROW */}
          <Box
            sx={{
              fontSize: { xs: '20px', md: '28px' },
              color: colors.accent.main,
              display: { xs: 'none', md: 'block' },
              transition: `all ${spacing.transitions.normal}`,
              animation: mounted && !prefersReducedMotion ? `${arrowBounce} 2.5s ease-in-out infinite` : 'none',
              filter: `drop-shadow(0 0 8px ${alpha(colors.accent.main, 0.3)})`,
            }}
          >
            →
          </Box>

          {/* TARGET PLATFORMS */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              sx={{
                fontSize: typography.fontSize.xs,
                fontFamily: typography.fontFamily.primary,
                fontWeight: typography.fontWeight.semibold,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: colors.text.tertiary,
                mb: 2,
              }}
            >
              Priority Target Platforms
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'center', md: 'flex-start' } }}>
              {targetPlatforms.map((platform, i) => (
                <Box
                  key={platform}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    animation: mounted && !prefersReducedMotion ? `${platformFadeIn} 0.5s ease-out ${0.5 + i * 0.1}s both` : 'none',
                    transition: `all ${spacing.transitions.normal}`,
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateX(4px)',
                    },
                    '&:hover .platform-dot': {
                      transform: 'scale(1.5)',
                      boxShadow: `0 0 12px ${alpha(colors.accent.main, 0.5)}`,
                    },
                    '&:hover .platform-name': {
                      color: colors.accent.main,
                      fontWeight: typography.fontWeight.semibold,
                    },
                  }}
                >
                  <Box
                    className="platform-dot"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: spacing.borderRadius.full,
                      background: colors.accent.main,
                      flexShrink: 0,
                      transition: `all ${spacing.transitions.normal}`,
                    }}
                  />
                  <Typography
                    className="platform-name"
                    sx={{
                      fontSize: { xs: typography.fontSize.sm, md: typography.fontSize.md },
                      fontFamily: typography.fontFamily.primary,
                      fontWeight: typography.fontWeight.medium,
                      letterSpacing: typography.letterSpacing.wide,
                      color: colors.text.secondary,
                      transition: `all ${spacing.transitions.normal}`,
                    }}
                  >
                    {platform}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── DIVIDER ─────────────────────────────────────── */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1080px',
          mx: 'auto',
          my: { xs: 6, md: 10 },
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${colors.border.primary}, transparent)`,
        }}
      />

      {/* ── CAPABILITIES ────────────────────────────────── */}
      <Box component="section" id="capabilities" aria-labelledby="capabilities-heading">
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Typography
            sx={{
              fontSize: typography.fontSize.xs,
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.semibold,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: colors.accent.main,
              mb: 2,
            }}
          >
            Capabilities
          </Typography>
          <Typography
            id="capabilities-heading"
            variant="h2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: { xs: '1.75rem', md: 'clamp(28px, 3.5vw, 40px)' },
              fontWeight: typography.fontWeight.bold,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              mb: { xs: 5, md: 7 },
              color: colors.text.primary,
            }}
          >
            Understand each source workload
            <Box component="span" sx={{ color: colors.text.tertiary, fontWeight: typography.fontWeight.regular }}>
              <br />before it reaches the target.
              {!isMobile && <br />}Not after.
            </Box>
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {capabilities.map((cap, i) => (
              <Box
                key={cap.title}
                sx={{
                  p: { xs: 3, md: '36px 32px' },
                  borderRadius: spacing.borderRadius.xl,
                  background: `linear-gradient(180deg, ${alpha(colors.background.card, 0.98)} 0%, ${alpha(colors.background.surface, 0.92)} 100%)`,
                  border: `1px solid ${alpha(colors.border.subtle, 0.72)}`,
                  transition: `all ${spacing.transitions.normal}`,
                  animation: anim(0.1 * i),
                  boxShadow: `0 8px 24px ${alpha(colors.neutral[900], 0.045)}`,
                  '&:hover': {
                    borderColor: alpha(colors.accent.main, 0.25),
                    boxShadow: `0 14px 34px ${alpha(colors.accent.main, 0.08)}, 0 0 0 1px ${alpha(colors.accent.main, 0.06)}`,
                    transform: 'translateY(-4px) scale(1.005)',
                    background: alpha(colors.background.card, 0.98),
                  },
                  '&:hover .icon-container': {
                    transform: 'scale(1.06)',
                    background: alpha(colors.accent.main, 0.12),
                  },
                }}
              >
                <Box
                  className="icon-container"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: spacing.borderRadius.lg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    background: alpha(colors.accent.main, 0.08),
                    color: colors.accent.main,
                    transition: `all ${spacing.transitions.normal}`,
                    boxShadow: `inset 0 0 0 1px ${alpha(colors.accent.main, 0.08)}`,
                  }}
                >
                  {cap.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: '17px',
                    fontWeight: typography.fontWeight.semibold,
                    mb: 1.25,
                    letterSpacing: '-0.01em',
                    color: colors.text.primary,
                  }}
                >
                  {cap.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: '14px',
                    lineHeight: 1.7,
                    color: colors.text.secondary,
                    fontWeight: typography.fontWeight.regular,
                  }}
                >
                  {cap.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── HOW IT WORKS ────────────────────────────────── */}
      <Box
        component="section"
        id="workflow"
        aria-labelledby="how-it-works-heading"
        sx={{
          background: `linear-gradient(180deg, ${alpha(colors.background.card, 0.96)} 0%, ${alpha(colors.background.surface, 0.96)} 100%)`,
          py: { xs: 8, md: 12 },
          borderTop: `1px solid ${alpha(colors.border.primary, 0.58)}`,
          borderBottom: `1px solid ${alpha(colors.border.primary, 0.58)}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: typography.fontSize.xs,
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.semibold,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: colors.accent.main,
              mb: 2,
            }}
          >
            How it works
          </Typography>
          <Typography
            id="how-it-works-heading"
            variant="h2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: { xs: '1.75rem', md: 'clamp(28px, 3.5vw, 40px)' },
              fontWeight: typography.fontWeight.bold,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              mb: { xs: 5, md: 7 },
              color: colors.text.primary,
            }}
          >
            From source backlog
            <Box component="span" sx={{ color: colors.text.tertiary, fontWeight: typography.fontWeight.regular }}>
              <br />to BigQuery, Delta Lake, or Databricks roadmap.
            </Box>
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 0,
              position: 'relative',
            }}
          >
            {/* Connecting line (desktop) */}
            <Box
              aria-hidden="true"
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: '28px',
                left: '16.66%',
                right: '16.66%',
                height: '1px',
                background: `linear-gradient(90deg, ${alpha(colors.accent.main, 0.15)}, ${alpha(colors.accent.main, 0.3)}, ${alpha(colors.accent.main, 0.15)})`,
              }}
            />
            {steps.map((step, i) => (
              <Box
                key={step.num}
                sx={{
                  textAlign: 'center',
                  px: { xs: 2, md: 3 },
                  pb: { xs: i < steps.length - 1 ? 4 : 0, md: 0 },
                  animation: anim(0.1 * i),
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: spacing.borderRadius.full,
                    background: colors.background.surface,
                    border: `2px solid ${alpha(colors.accent.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    fontSize: '18px',
                    fontWeight: typography.fontWeight.semibold,
                    fontFamily: typography.fontFamily.primary,
                    color: colors.accent.main,
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: `0 8px 18px ${alpha(colors.accent.main, 0.1)}`,
                  }}
                >
                  {step.num}
                </Box>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: '17px',
                    fontWeight: typography.fontWeight.semibold,
                    mb: 1,
                    color: colors.text.primary,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: typography.fontSize.sm,
                    lineHeight: 1.65,
                    color: colors.text.secondary,
                    fontWeight: typography.fontWeight.regular,
                  }}
                >
                  {step.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── BOTTOM CTA ──────────────────────────────────── */}
      <Box
        component="section"
        aria-labelledby="cta-heading"
        sx={{ py: { xs: 8, md: 12 }, textAlign: 'center', background: colors.background.surface }}
      >
        <Container
          maxWidth="sm"
          sx={{
            py: { xs: 4, md: 5 },
            px: { xs: 3, md: 5 },
            borderRadius: '28px',
            background: `linear-gradient(180deg, ${alpha(colors.background.card, 0.96)} 0%, ${alpha(colors.background.surface, 0.94)} 100%)`,
            border: `1px solid ${alpha(colors.border.primary, 0.74)}`,
            boxShadow: `0 14px 36px ${alpha(colors.neutral[900], 0.05)}`,
          }}
        >
          <Typography
            id="cta-heading"
            variant="h2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: { xs: '1.75rem', md: 'clamp(28px, 3vw, 36px)' },
              fontWeight: typography.fontWeight.bold,
              letterSpacing: '-0.03em',
              mb: 2,
              color: colors.text.primary,
            }}
          >
            Start with the roadmap systems.
          </Typography>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.primary,
              fontSize: '17px',
              color: colors.text.secondary,
              lineHeight: typography.lineHeight.relaxed,
              mb: 5,
              fontWeight: typography.fontWeight.regular,
            }}
          >
            Bring a Snowflake, AWS Redshift, Microsoft SQL Server, Oracle, or Azure Synapse workload.
            Archer will turn it into a reviewable target migration plan.
          </Typography>
          <Box
            component="button"
            onClick={onGetStarted}
            sx={{
              py: { xs: 2, md: '15px' },
              px: { xs: 3.5, md: '40px' },
              minHeight: { xs: '48px', md: 'auto' },
              fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily.primary,
              color: colors.text.onPrimary,
              background: `linear-gradient(135deg, ${colors.accent.main} 0%, ${colors.accent.hover} 100%)`,
              border: 'none',
              borderRadius: spacing.borderRadius.lg,
              cursor: 'pointer',
              transition: `background ${spacing.transitions.normal}, transform ${spacing.transitions.normal}, box-shadow ${spacing.transitions.normal}`,
              letterSpacing: typography.letterSpacing.wide,
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.accent.hover} 0%, ${colors.accent.active} 100%)`,
                transform: 'translateY(-1px)',
                boxShadow: `0 12px 34px ${alpha(colors.accent.main, 0.22)}`,
              },
              '&:active': { transform: 'translateY(0)' },
              boxShadow: `0 8px 24px ${alpha(colors.accent.main, 0.18)}`,
              ...focusVisible,
            }}
          >
            Request Assessment
          </Box>
        </Container>
      </Box>

      {/* ── FOOTER ──────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          py: { xs: 4, md: 5 },
          px: { xs: 3, md: 7 },
          borderTop: `1px solid ${alpha(colors.border.subtle, 0.9)}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'space-between' },
          alignItems: 'center',
          maxWidth: '1180px',
          mx: 'auto',
          width: '100%',
          gap: { xs: 1.5, sm: 2 },
          position: 'relative',
        }}
      >
        <Typography sx={{ fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.primary, color: colors.text.tertiary }}>
          {isWhiteLabelEnabled ? whiteLabelSettings.footerText : `\u00A9 ${new Date().getFullYear()} Tenarai`}
        </Typography>
        <Box component="nav" aria-label="Footer navigation" sx={{ display: 'flex', gap: { xs: 2.5, sm: 3.5 } }}>
          {(isWhiteLabelEnabled && whiteLabelSettings.footerLinks?.length > 0
            ? whiteLabelSettings.footerLinks
            : [
                { label: 'Privacy', url: '#' },
                { label: 'Terms', url: '#' },
                { label: 'Contact', url: '#' },
              ]
          ).map((link) => (
            <Typography
              key={link.label}
              component="a"
              href={link.url}
              sx={{
                fontSize: typography.fontSize.sm,
                fontFamily: typography.fontFamily.primary,
                color: alpha(colors.text.tertiary, 0.9),
                textDecoration: 'none',
                transition: `color ${spacing.transitions.fast}`,
                '&:hover': { color: colors.text.primary },
                ...focusVisible,
              }}
            >
              {link.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default LandingPage
